import { useState } from "react";
import { message } from "antd";
import {
  quoteTransition,
  quotationEmail,
  checkSignedQuoteFile,
  saveQuoteWorkflow,
  downloadSignedQuote,
} from "../../services/quotationWorkflow";

const input =
  "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm";
const button =
  "rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50";
export default function QuotationWorkflow({
  quotation,
  client,
  onChange,
  onRevision,
  onDownload,
}) {
  const [record, setRecord] = useState(() => quotation.workflow || {});
  const [action, setAction] = useState("Envoyé");
  const [note, setNote] = useState("");
  const [recipient, setRecipient] = useState(
    client.contactEMail ||
      client.contacts?.find((contact) => contact.email)?.email ||
      "",
  );
  const [signer, setSigner] = useState("");
  const [signedAt, setSignedAt] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  let email = "";
  try {
    email = quotationEmail(quotation, client, recipient, note);
  } catch {}
  const author =
    [localStorage.getItem("firstname"), localStorage.getItem("lastname")]
      .filter(Boolean)
      .join(" ") || "Freelance";
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (action === "Envoyé" && !email)
        throw new Error("Indiquez l’email du destinataire.");
      if (file) checkSignedQuoteFile(file);
      const change = {
        state: action,
        note:
          action === "Envoyé" ? `Destinataire : ${recipient}\n${note}` : note,
        author,
        signer,
        signedAt,
      };
      quoteTransition(record, { ...change, fileKey: file ? "pending" : undefined });
      const next = await saveQuoteWorkflow(quotation._id, { ...change, file });
      setRecord(next);
      setNote("");
      setFile(null);
      onChange(next);
      message.success("Décision enregistrée.");
    } catch (error) {
      message.error(error.message || "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };
  const proof = () =>
    downloadSignedQuote(quotation._id).catch(() =>
      message.error("Devis signé indisponible."),
    );
  const terminal = ["Signé", "Remplacé"].includes(record.state);
  return (
    <div className="space-y-5">
      <p className="rounded-lg bg-blue-50 p-3 text-sm text-slate-600">
        Suivi enregistré et partagé avec l’agence. L’email
        s’ouvre dans votre messagerie ; ajoutez le PDF et envoyez-le. La
        signature ci-dessous est une déclaration avec justificatif, pas une
        signature électronique.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-semibold text-slate-900">
          État : {record.state || (quotation.isDraft ? "Brouillon" : "Créé")}
          {record.state && " (déclaré)"}
        </p>
        <button className={button} disabled={terminal} onClick={onRevision}>
          Modifier pour négociation
        </button>
      </div>
      {!terminal && (
        <form onSubmit={save} className="space-y-4">
          <label className="block text-sm font-medium">
            Action
            <select
              className={input}
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setFile(null);
              }}
            >
              {["Envoyé", "En négociation", "Signé", "Refusé"].map((state) => (
                <option key={state}>{state}</option>
              ))}
            </select>
          </label>
          {action === "Envoyé" && (
            <div className="rounded-lg border border-slate-200 p-4">
              <label className="block text-sm">
                Email du destinataire
                <input
                  type="email"
                  required
                  className={input}
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className={button} onClick={onDownload}>
                  1. Télécharger le PDF
                </button>
                {email ? (
                  <a className={button} href={email}>
                    2. Préparer l’email
                  </a>
                ) : (
                  <span className="text-xs text-slate-500">
                    Renseignez un email valide.
                  </span>
                )}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Enregistrez l’envoi après l’avoir effectué dans votre
                messagerie.
              </p>
            </div>
          )}
          {action === "Signé" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                Signataire
                <input
                  required
                  className={input}
                  value={signer}
                  onChange={(e) => setSigner(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                Date de signature
                <input
                  required
                  type="date"
                  className={input}
                  value={signedAt}
                  onChange={(e) => setSignedAt(e.target.value)}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                Devis signé (PDF, 10 Mo maximum)
                <input
                  required
                  type="file"
                  accept="application/pdf"
                  className={input}
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
              </label>
            </div>
          )}
          <label className="block text-sm font-medium">
            {action === "Refusé"
              ? "Motif du refus"
              : action === "En négociation"
                ? "Modifications demandées par le client"
                : "Commentaire"}
            <textarea
              required={["Refusé", "En négociation"].includes(action)}
              rows={3}
              className={input}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <button
            disabled={busy}
            className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
            type="submit"
          >
            {busy ? "Enregistrement…" : `Enregistrer : ${action.toLowerCase()}`}
          </button>
        </form>
      )}
      <section>
        <h3 className="font-semibold text-slate-900">Historique</h3>
        {!record.history?.length && (
          <p className="mt-2 text-sm text-slate-500">Aucune action déclarée.</p>
        )}
        {(record.history || []).map((event, index) => (
          <article key={index} className="mt-3 border-t border-slate-100 pt-3">
            <p className="text-sm font-semibold">{event.state} (déclaré)</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
              {event.note}
            </p>
            {event.signer && (
              <p className="text-sm">
                Signataire : {event.signer} · {event.signedAt}
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500">
              {event.author} · {new Date(event.savedAt).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" })}
            </p>
            {event.hasSignedPdf && (
              <button className={`${button} mt-2`} onClick={proof}>
                Télécharger le devis signé
              </button>
            )}
            {event.revisionId && (
              <p className="mt-1 text-xs text-slate-500">
                Nouvelle version : {event.revisionId}
              </p>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
